import React, { useState, useEffect } from 'react';
import {
  Box, Typography, CircularProgress, IconButton, Button,
  Select, MenuItem, FormControl, InputLabel,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import DirectionsIcon from '@material-ui/icons/Directions';
import SearchIcon from '@material-ui/icons/Search';
import DeleteIcon from '@material-ui/icons/Delete';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CloseIcon from '@material-ui/icons/Close';
import RoomIcon from '@material-ui/icons/Room';
import FlagIcon from '@material-ui/icons/Flag';
import TimerIcon from '@material-ui/icons/Timer';
import LocalShippingIcon from '@material-ui/icons/LocalShipping';
import PersonIcon from '@material-ui/icons/Person';
import CalendarTodayIcon from '@material-ui/icons/CalendarToday';
import SendIcon from '@material-ui/icons/Send';
import ViewListIcon from '@material-ui/icons/ViewList';
import ViewModuleIcon from '@material-ui/icons/ViewModule';
import HotelIcon from '@material-ui/icons/Hotel';
import PauseCircleFilledIcon from '@material-ui/icons/PauseCircleFilled';
import { COLORS } from '../../theme';

const API_BASE = '/planner-api';

const STATUS_STYLES = {
  draft: { bg: 'rgba(148,163,184,0.1)', color: '#64748b', label: 'Rascunho', gradient: 'linear-gradient(135deg, #94a3b8, #64748b)' },
  planned: { bg: 'rgba(59,130,246,0.08)', color: '#3b82f6', label: 'Planeada', gradient: 'linear-gradient(135deg, #3b82f6, #2563eb)' },
  in_progress: { bg: 'rgba(245,158,11,0.08)', color: '#f59e0b', label: 'Em Trânsito', gradient: 'linear-gradient(135deg, #f59e0b, #d97706)' },
  completed: { bg: 'rgba(34,197,94,0.08)', color: '#22c55e', label: 'Concluida', gradient: 'linear-gradient(135deg, #22c55e, #16a34a)' },
  cancelled: { bg: 'rgba(239,68,68,0.08)', color: '#ef4444', label: 'Cancelada', gradient: 'linear-gradient(135deg, #ef4444, #dc2626)' },
};

const useStyles = makeStyles(() => ({
  '@global': {
    '@keyframes slideInUp': {
      '0%': { opacity: 0, transform: 'translateY(20px)' },
      '100%': { opacity: 1, transform: 'translateY(0)' },
    },
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 22,
    background: 'var(--adm-header-bg, #e2f1e9)',
    border: '1px solid var(--adm-header-border, #bfe0d1)',
    borderRadius: 14,
    padding: '18px 22px',
  },
  titleWrap: {
    display: 'flex',
    alignItems: 'center',
    gap: 14,
  },
  titleIcon: {
    width: 44,
    height: 44,
    borderRadius: 12,
    background: 'rgba(59,130,246,0.08)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  title: {
    fontSize: '1.72rem',
    fontWeight: 700,
    color: COLORS.text,
    letterSpacing: '-0.02em',
  },
  subtitle: {
    fontSize: '0.78rem',
    color: COLORS.textSecondary,
    marginTop: 2,
  },
  filtersRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginBottom: 20,
    flexWrap: 'wrap',
  },
  searchBox: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    background: 'var(--adm-input-bg, #ffffff)',
    border: '1.5px solid var(--adm-input-border, #e2e8f0)',
    borderRadius: 12,
    padding: '0 14px',
    height: 42,
    maxWidth: 340,
    flex: 1,
    minWidth: 200,
    boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
    transition: 'all 0.25s ease',
    '&:focus-within': {
      borderColor: '#3b82f6',
      boxShadow: '0 0 0 4px rgba(59,130,246,0.08)',
    },
  },
  filterSelect: {
    minWidth: 160,
    background: 'var(--adm-input-bg, #fff)',
    borderRadius: 12,
    '& .MuiOutlinedInput-root': {
      borderRadius: 12,
      height: 42,
      fontSize: '0.82rem',
    },
  },
  dateInput: {
    height: 42,
    border: '1.5px solid var(--adm-input-border, #e2e8f0)',
    borderRadius: 12,
    padding: '0 12px',
    fontSize: '0.82rem',
    color: COLORS.text,
    background: 'var(--adm-input-bg, #fff)',
    outline: 'none',
    cursor: 'pointer',
    transition: 'all 0.2s',
    '&:focus': {
      borderColor: '#3b82f6',
      boxShadow: '0 0 0 4px rgba(59,130,246,0.08)',
    },
  },
  viewToggle: {
    display: 'flex',
    background: 'var(--adm-hover, #f1f5f9)',
    borderRadius: 10,
    padding: 3,
    gap: 2,
  },
  viewBtn: {
    padding: '6px 8px',
    borderRadius: 8,
    border: 'none',
    background: 'transparent',
    color: 'var(--adm-text-muted, #94a3b8)',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s',
  },
  viewBtnActive: {
    background: 'var(--adm-card, #fff)',
    color: '#3b82f6',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
  },
  // Cards view
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
    gap: 16,
  },
  routeCard: {
    background: 'var(--adm-card, #ffffff)',
    borderRadius: 14,
    border: '1px solid var(--adm-card-border, rgba(0,0,0,0.05))',
    boxShadow: '0 1px 2px rgba(20,42,70,0.04), 0 12px 32px rgba(20,42,70,0.07)',
    overflow: 'hidden',
    transition: 'all 0.25s ease',
    cursor: 'pointer',
    animation: '$slideInUp 0.4s ease-out',
    '&:hover': {
      transform: 'translateY(-3px)',
      boxShadow: '0 8px 28px rgba(0,0,0,0.08)',
    },
  },
  cardAccent: {
    height: 3,
  },
  cardBody: {
    padding: '16px 18px 12px',
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  cardTitle: {
    fontSize: '0.92rem',
    fontWeight: 700,
    color: COLORS.text,
    lineHeight: 1.2,
  },
  cardRef: {
    fontSize: '0.68rem',
    color: COLORS.textMuted,
    marginTop: 2,
  },
  cardBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    padding: '3px 9px',
    borderRadius: 6,
    fontSize: '0.6rem',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    flexShrink: 0,
  },
  cardRoute: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: 10,
    padding: '8px 10px',
    background: 'var(--adm-hover, #f8fafc)',
    borderRadius: 8,
  },
  cardRouteText: {
    flex: 1,
    minWidth: 0,
  },
  cardRouteName: {
    fontSize: '0.74rem',
    fontWeight: 500,
    color: COLORS.text,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  cardStats: {
    display: 'flex',
    gap: 16,
    marginBottom: 10,
  },
  cardStat: {
    display: 'flex',
    alignItems: 'center',
    gap: 5,
  },
  cardStatValue: {
    fontSize: '0.78rem',
    fontWeight: 600,
    color: COLORS.text,
  },
  cardStatLabel: {
    fontSize: '0.64rem',
    color: COLORS.textMuted,
  },
  cardFooter: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '10px 18px 12px',
    borderTop: '1px solid var(--adm-border, rgba(0,0,0,0.04))',
  },
  cardMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  cardMetaItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    fontSize: '0.68rem',
    color: COLORS.textMuted,
  },
  deleteBtn: {
    color: 'var(--adm-text-muted, #94a3b8)',
    transition: 'all 0.2s',
    '&:hover': { color: 'var(--adm-danger, #ef4444)', background: 'rgba(239,68,68,0.06)' },
  },
  loading: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: 300,
  },
  emptyState: {
    textAlign: 'center',
    padding: '60px 20px',
    color: COLORS.textMuted,
  },
  // Table view
  tableCard: {
    background: 'var(--adm-card, #ffffff)',
    borderRadius: 16,
    border: '1px solid var(--adm-card-border, rgba(0,0,0,0.05))',
    boxShadow: '0 1px 2px rgba(20,42,70,0.04), 0 12px 32px rgba(20,42,70,0.07)',
    overflow: 'auto', // scroll horizontal (tabela larga)
    maxHeight: 'calc(100vh - 230px)', // scroll vertical interno → cabeçalho sticky funciona
    animation: '$slideInUp 0.4s ease-out',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    minWidth: 1100,
  },
  th: {
    textAlign: 'left',
    padding: '14px 16px',
    fontSize: '0.66rem',
    fontWeight: 700,
    color: COLORS.textMuted,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    borderBottom: '1px solid var(--adm-border, rgba(0,0,0,0.06))',
    background: 'var(--adm-thead-bg, #f8fafc)',
    whiteSpace: 'nowrap',
    position: 'sticky', top: 0, zIndex: 2,
  },
  td: {
    padding: '13px 16px',
    fontSize: '0.82rem',
    color: COLORS.text,
    borderBottom: '1px solid var(--adm-border, rgba(0,0,0,0.03))',
  },
  tableRow: {
    transition: 'background 0.15s',
    cursor: 'pointer',
    '&:hover': { background: 'var(--adm-hover, #f8fafc)' },
    '&:last-child td': { borderBottom: 'none' },
  },
  badge: {
    display: 'inline-block',
    padding: '4px 11px',
    borderRadius: 6,
    fontSize: '0.66rem',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  truncate: {
    maxWidth: 160,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  // ── Células ricas da tabela ────────────────────────────────────────
  // Percurso: origem → destino com pontos coloridos (verde parte, vermelho chega)
  legRow: { display: 'flex', alignItems: 'center', gap: 7, fontSize: '0.78rem', color: COLORS.text },
  dotFrom: { width: 7, height: 7, borderRadius: '50%', background: '#16a34a', flexShrink: 0 },
  dotTo: { width: 7, height: 7, borderRadius: '50%', background: '#dc2626', flexShrink: 0 },
  // Métricas alinhadas à direita, em monoespaçado (colunas de números lêem-se melhor)
  metricMain: { fontSize: '0.84rem', fontWeight: 700, color: COLORS.text, fontFamily: '"SF Mono", "Fira Code", monospace' },
  metricSub: { fontSize: '0.7rem', color: COLORS.textMuted, fontFamily: '"SF Mono", "Fira Code", monospace' },
  // Recursos: viatura + motorista
  resRow: { display: 'flex', alignItems: 'center', gap: 6 },
  resIcon: { fontSize: 13, color: 'var(--adm-text-muted, #94a3b8)', flexShrink: 0 },
  resTxt: { fontSize: '0.76rem', color: COLORS.text },
  resNone: { fontSize: '0.72rem', color: 'var(--adm-text-muted, #cbd5e1)', fontStyle: 'italic' },
  plate: {
    fontSize: '0.72rem', fontWeight: 700, fontFamily: '"SF Mono", "Fira Code", monospace',
    background: 'var(--adm-hover, #f1f5f9)', color: 'var(--adm-text, #334155)', padding: '1px 7px', borderRadius: 5, border: '1px solid var(--adm-card-border, #e2e8f0)',
  },
  // Detail popup
  overlay: {
    position: 'fixed',
    top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(0,0,0,0.5)',
    backdropFilter: 'blur(4px)',
    zIndex: 9999,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  detailDialog: {
    background: 'var(--adm-card, #fff)',
    borderRadius: 16,
    width: 560,
    maxWidth: '94vw',
    maxHeight: '90vh',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  detailHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 20px',
    background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
    color: '#fff',
    borderRadius: '16px 16px 0 0',
    flexShrink: 0,
  },
  detailBody: {
    padding: '20px',
    overflowY: 'auto',
    flex: 1,
    minHeight: 0,
  },
  detailSection: {
    marginBottom: 18,
  },
  detailSectionLabel: {
    fontSize: '0.6rem',
    fontWeight: 700,
    color: 'var(--adm-text-muted, #94a3b8)',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    marginBottom: 8,
  },
  detailCard: {
    background: 'var(--adm-hover, #f8fafc)',
    borderRadius: 10,
    border: '1px solid var(--adm-card-border, #e2e8f0)',
    padding: '12px 14px',
  },
  detailRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: 6,
    '&:last-child': { marginBottom: 0 },
  },
  detailLabel: {
    fontSize: '0.72rem',
    color: 'var(--adm-text-dim, #64748b)',
    minWidth: 90,
  },
  detailValue: {
    fontSize: '0.78rem',
    fontWeight: 600,
    color: 'var(--adm-text, #1e293b)',
    flex: 1,
  },
  detailStatsGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 10,
  },
  detailStatCard: {
    background: 'var(--adm-hover, #f8fafc)',
    borderRadius: 10,
    border: '1px solid var(--adm-card-border, #e2e8f0)',
    padding: '10px 12px',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  detailStatIcon: {
    width: 32,
    height: 32,
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  detailFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 10,
    padding: '14px 20px',
    borderTop: '1px solid var(--adm-border, #e2e8f0)',
    background: 'var(--adm-hover, #f8fafc)',
    borderRadius: '0 0 16px 16px',
    flexShrink: 0,
  },
  resultsCount: {
    fontSize: '0.75rem',
    color: COLORS.textMuted,
    padding: '8px 0',
  },
  // Waypoints timeline
  timeline: {
    position: 'relative',
    paddingLeft: 20,
  },
  timelineLine: {
    position: 'absolute',
    left: 5,
    top: 14,
    bottom: 14,
    width: 2,
    background: 'linear-gradient(to bottom, #22c55e, #3b82f6, #ef4444)',
    borderRadius: 1,
  },
  waypointItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '7px 0',
    position: 'relative',
  },
  waypointDot: {
    width: 10,
    height: 10,
    borderRadius: '50%',
    flexShrink: 0,
    position: 'absolute',
    left: -20,
    border: '2px solid #fff',
    zIndex: 1,
  },
  waypointLabel: {
    fontSize: '0.56rem',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    lineHeight: 1,
  },
  waypointName: {
    fontSize: '0.76rem',
    fontWeight: 600,
    color: 'var(--adm-text, #1e293b)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    lineHeight: 1.3,
  },
  // Stops
  stopTimeline: {
    display: 'flex',
    flexDirection: 'column',
    gap: 0,
  },
  stopItem: {
    display: 'flex',
    gap: 10,
    padding: '8px 0',
    borderBottom: '1px solid var(--adm-border, #f1f5f9)',
    alignItems: 'center',
    '&:last-child': { borderBottom: 'none' },
  },
  stopIcon: {
    width: 28,
    height: 28,
    borderRadius: 7,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  stopInfo: {
    flex: 1,
    minWidth: 0,
  },
  stopAddress: {
    fontSize: '0.74rem',
    fontWeight: 600,
    color: 'var(--adm-text, #1e293b)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    lineHeight: 1.2,
  },
  stopMeta: {
    display: 'flex',
    gap: 8,
    marginTop: 2,
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  stopMetaItem: {
    fontSize: '0.62rem',
    color: 'var(--adm-text-dim, #64748b)',
    display: 'flex',
    alignItems: 'center',
    gap: 2,
  },
  stopTypeBadge: {
    display: 'inline-block',
    padding: '1px 6px',
    borderRadius: 4,
    fontSize: '0.56rem',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  },
}));

function formatDuration(seconds) {
  if (!seconds) return '-';
  const h = Math.floor(seconds / 3600);
  const m = Math.round((seconds % 3600) / 60);
  if (h > 0) return `${h}h ${m}min`;
  return `${m}min`;
}

function formatDistance(meters) {
  if (!meters) return '-';
  return `${(meters / 1000).toFixed(1)} km`;
}

function formatDate(dateStr) {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  return d.toLocaleDateString('pt-PT', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function formatDateTime(dateStr) {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  return d.toLocaleString('pt-PT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

const STOP_TYPES = {
  'break-45': { label: 'Pausa 45min', color: '#f59e0b', bg: 'rgba(245,158,11,0.08)' },
  'break-15': { label: 'Pausa 15min', color: '#f59e0b', bg: 'rgba(245,158,11,0.08)' },
  'break-30': { label: 'Pausa 30min', color: '#f59e0b', bg: 'rgba(245,158,11,0.08)' },
  'daily-rest': { label: 'Descanso Diario', color: '#8b5cf6', bg: 'rgba(139,92,246,0.08)' },
  'rest': { label: 'Descanso', color: '#8b5cf6', bg: 'rgba(139,92,246,0.08)' },
};

function formatStopDuration(minutes) {
  if (!minutes) return '-';
  if (minutes >= 60) {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return m > 0 ? `${h}h ${m}min` : `${h}h`;
  }
  return `${minutes}min`;
}

function AdminRoutes({ token }) {
  const classes = useStyles();
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [clientFilter, setClientFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [viewMode, setViewMode] = useState('cards');
  const [selectedRoute, setSelectedRoute] = useState(null);

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadRoutes(); }, []);

  async function loadRoutes() {
    try {
      const res = await fetch(`${API_BASE}/routes/admin/all`, { headers });
      if (res.ok) setRoutes(await res.json());
    } catch (err) {
      console.error('Admin routes load error:', err);
    }
    setLoading(false);
  }

  async function handleDelete(e, id) {
    e.stopPropagation();
    if (!window.confirm('Tem a certeza que deseja eliminar esta rota?')) return;
    try {
      const res = await fetch(`${API_BASE}/routes/${id}`, { method: 'DELETE', headers });
      if (res.ok) {
        setRoutes(prev => prev.filter(r => r.id !== id));
        if (selectedRoute?.id === id) setSelectedRoute(null);
      }
    } catch (err) {
      console.error('Route delete error:', err);
    }
  }

  // Unique clients for filter
  const clients = [...new Map(routes.map(r => [r.client?.name, r.client]).filter(([k]) => k)).values()];

  const filtered = routes.filter(r => {
    const q = search.toLowerCase();
    const matchSearch = !q ||
      (r.name || '').toLowerCase().includes(q) ||
      (r.cargoRef || '').toLowerCase().includes(q) ||
      (r.origin?.name || '').toLowerCase().includes(q) ||
      (r.destination?.name || '').toLowerCase().includes(q) ||
      (r.vehicle?.plate || '').toLowerCase().includes(q) ||
      (r.creator?.name || '').toLowerCase().includes(q);
    const matchClient = !clientFilter || r.client?.name === clientFilter;

    // Date filter
    let matchDate = true;
    if (dateFrom) {
      const from = new Date(dateFrom);
      from.setHours(0, 0, 0, 0);
      matchDate = matchDate && new Date(r.createdAt) >= from;
    }
    if (dateTo) {
      const to = new Date(dateTo);
      to.setHours(23, 59, 59, 999);
      matchDate = matchDate && new Date(r.createdAt) <= to;
    }

    return matchSearch && matchClient && matchDate;
  });

  if (loading) {
    return <Box className={classes.loading}><CircularProgress size={36} style={{ color: '#3b82f6' }} /></Box>;
  }

  return (
    <Box>
      {/* Header */}
      <Box className={classes.header}>
        <Box className={classes.titleWrap}>
          <Box className={classes.titleIcon}>
            <DirectionsIcon style={{ color: '#3b82f6', fontSize: 22 }} />
          </Box>
          <Box>
            <Typography className={classes.title}>Rotas</Typography>
            <Typography className={classes.subtitle}>{routes.length} rotas guardadas na plataforma</Typography>
          </Box>
        </Box>
        <Box className={classes.viewToggle}>
          <button
            className={`${classes.viewBtn} ${viewMode === 'cards' ? classes.viewBtnActive : ''}`}
            onClick={() => setViewMode('cards')}
          >
            <ViewModuleIcon style={{ fontSize: 18 }} />
          </button>
          <button
            className={`${classes.viewBtn} ${viewMode === 'table' ? classes.viewBtnActive : ''}`}
            onClick={() => setViewMode('table')}
          >
            <ViewListIcon style={{ fontSize: 18 }} />
          </button>
        </Box>
      </Box>

      {/* Filters */}
      <Box className={classes.filtersRow}>
        <Box className={classes.searchBox}>
          <SearchIcon style={{ color: 'var(--adm-text-muted, #94a3b8)', fontSize: 18 }} />
          <input
            style={{ border: 'none', outline: 'none', flex: 1, fontSize: '0.82rem', color: COLORS.text, background: 'transparent' }}
            placeholder="Pesquisar por nome, origem, destino, matricula..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </Box>
        <FormControl variant="outlined" size="small" className={classes.filterSelect}>
          <InputLabel>Organização</InputLabel>
          <Select
            label="Organização"
            value={clientFilter}
            onChange={e => setClientFilter(e.target.value)}
          >
            <MenuItem value="">Todos</MenuItem>
            {clients.map(c => (
              <MenuItem key={c.name} value={c.name}>{c.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Box style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <CalendarTodayIcon style={{ color: 'var(--adm-text-muted, #94a3b8)', fontSize: 16 }} />
          <input
            type="date"
            className={classes.dateInput}
            value={dateFrom}
            onChange={e => setDateFrom(e.target.value)}
            title="Data inicial"
          />
          <Typography style={{ fontSize: '0.75rem', color: 'var(--adm-text-muted, #94a3b8)' }}>a</Typography>
          <input
            type="date"
            className={classes.dateInput}
            value={dateTo}
            onChange={e => setDateTo(e.target.value)}
            title="Data final"
          />
          {(dateFrom || dateTo) && (
            <IconButton size="small" onClick={() => { setDateFrom(''); setDateTo(''); }} style={{ color: 'var(--adm-text-muted, #94a3b8)', padding: 4 }}>
              <CloseIcon style={{ fontSize: 16 }} />
            </IconButton>
          )}
        </Box>
      </Box>

      {/* Results count */}
      <Typography className={classes.resultsCount}>
        {filtered.length} {filtered.length === 1 ? 'rota encontrada' : 'rotas encontradas'}
        {(search || clientFilter || dateFrom || dateTo) ? ' (filtrado)' : ''}
      </Typography>

      {/* Empty state */}
      {filtered.length === 0 ? (
        <Box className={classes.emptyState}>
          <DirectionsIcon style={{ fontSize: 48, color: 'var(--adm-text-muted, #e2e8f0)', marginBottom: 12 }} />
          <Typography style={{ fontSize: '0.9rem', color: COLORS.textMuted }}>Nenhuma rota encontrada</Typography>
          <Typography style={{ fontSize: '0.75rem', color: 'var(--adm-text-muted, #cbd5e1)', marginTop: 4 }}>Tente ajustar os filtros de pesquisa</Typography>
        </Box>
      ) : viewMode === 'cards' ? (
        /* Cards View */
        <Box className={classes.grid}>
          {filtered.map((route, i) => {
            const st = STATUS_STYLES[route.status] || STATUS_STYLES.draft;
            return (
              <Box key={route.id} className={classes.routeCard} style={{ animationDelay: `${i * 0.04}s` }} onClick={() => setSelectedRoute(route)}>
                <Box className={classes.cardAccent} style={{ background: st.gradient }} />
                <Box className={classes.cardBody}>
                  <Box className={classes.cardHeader}>
                    <Box style={{ flex: 1, minWidth: 0 }}>
                      <Typography className={classes.cardTitle}>{route.name || 'Rota sem nome'}</Typography>
                      {route.cargoRef && <Typography className={classes.cardRef}>{route.cargoRef}</Typography>}
                    </Box>
                    <span className={classes.cardBadge} style={{ background: st.bg, color: st.color }}>
                      {st.label}
                    </span>
                  </Box>

                  {/* Origin → Destination */}
                  <Box className={classes.cardRoute}>
                    <RoomIcon style={{ fontSize: 14, color: '#22c55e', flexShrink: 0 }} />
                    <Box className={classes.cardRouteText}>
                      <Typography className={classes.cardRouteName}>{route.origin?.name || '-'}</Typography>
                    </Box>
                    <Typography style={{ fontSize: '0.7rem', color: 'var(--adm-text-muted, #94a3b8)', margin: '0 4px' }}>→</Typography>
                    <FlagIcon style={{ fontSize: 14, color: '#ef4444', flexShrink: 0 }} />
                    <Box className={classes.cardRouteText}>
                      <Typography className={classes.cardRouteName}>{route.destination?.name || '-'}</Typography>
                    </Box>
                  </Box>

                  {/* Stats */}
                  <Box className={classes.cardStats}>
                    <Box className={classes.cardStat}>
                      <DirectionsIcon style={{ fontSize: 14, color: '#3b82f6' }} />
                      <Typography className={classes.cardStatValue}>{formatDistance(route.totalDistanceM)}</Typography>
                    </Box>
                    <Box className={classes.cardStat}>
                      <TimerIcon style={{ fontSize: 14, color: '#f59e0b' }} />
                      <Typography className={classes.cardStatValue}>{formatDuration(route.totalDurationS)}</Typography>
                    </Box>
                    {route.sentToVehicle && (
                      <Box className={classes.cardStat}>
                        <CheckCircleIcon style={{ fontSize: 14, color: '#22c55e' }} />
                        <Typography className={classes.cardStatValue} style={{ color: '#22c55e', fontSize: '0.72rem' }}>Enviada</Typography>
                      </Box>
                    )}
                  </Box>
                </Box>

                {/* Footer */}
                <Box className={classes.cardFooter}>
                  <Box className={classes.cardMeta}>
                    <Box className={classes.cardMetaItem}>
                      <PersonIcon style={{ fontSize: 13 }} />
                      {route.client?.name || '-'}
                    </Box>
                    <Box className={classes.cardMetaItem}>
                      <CalendarTodayIcon style={{ fontSize: 12 }} />
                      {formatDate(route.createdAt)}
                    </Box>
                  </Box>
                  <IconButton size="small" onClick={(e) => handleDelete(e, route.id)} className={classes.deleteBtn}>
                    <DeleteIcon style={{ fontSize: 16 }} />
                  </IconButton>
                </Box>
              </Box>
            );
          })}
        </Box>
      ) : (
        /* Table View */
        <Box className={classes.tableCard}>
          <table className={classes.table}>
            <thead>
              <tr>
                {/* Colunas consolidadas: 13 colunas soltas ficavam ilegíveis.
                    Cada célula agrupa o que se lê em conjunto. */}
                <th className={classes.th}>Rota</th>
                <th className={classes.th}>Organização</th>
                <th className={classes.th}>Percurso</th>
                <th className={classes.th} style={{ textAlign: 'right' }}>Distância · Duração</th>
                <th className={classes.th}>Recursos</th>
                <th className={classes.th}>Estado</th>
                <th className={classes.th}>Criada</th>
                <th className={classes.th} style={{ width: 50 }}></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(route => {
                const st = STATUS_STYLES[route.status] || STATUS_STYLES.draft;
                return (
                  <tr key={route.id} className={classes.tableRow} onClick={() => setSelectedRoute(route)}>
                    {/* Rota — nome + referência de carga */}
                    <td className={classes.td}>
                      <Typography style={{ fontWeight: 700, fontSize: '0.85rem', color: COLORS.text }}>
                        {route.name || <span style={{ color: COLORS.textMuted, fontWeight: 500, fontStyle: 'italic' }}>Sem nome</span>}
                      </Typography>
                      <Typography style={{ fontSize: '0.68rem', color: COLORS.textMuted }}>
                        #{route.id}{route.cargoRef ? ` · ${route.cargoRef}` : ''}
                      </Typography>
                    </td>
                    {/* Organização */}
                    <td className={classes.td}>
                      <Typography style={{ fontSize: '0.82rem', fontWeight: 600, color: COLORS.text }}>{route.client?.name || '—'}</Typography>
                    </td>
                    {/* Percurso — origem → destino num só bloco */}
                    <td className={classes.td} style={{ maxWidth: 260 }}>
                      <Box className={classes.legRow}>
                        <span className={classes.dotFrom} />
                        <Box className={classes.truncate} title={route.origin?.name || '—'}>{route.origin?.name || '—'}</Box>
                      </Box>
                      <Box className={classes.legRow} style={{ marginTop: 2 }}>
                        <span className={classes.dotTo} />
                        <Box className={classes.truncate} title={route.destination?.name || '—'}>{route.destination?.name || '—'}</Box>
                      </Box>
                    </td>
                    {/* Distância · Duração */}
                    <td className={classes.td} style={{ textAlign: 'right' }}>
                      <Typography className={classes.metricMain}>{formatDistance(route.totalDistanceM)}</Typography>
                      <Typography className={classes.metricSub}>{formatDuration(route.totalDurationS)}</Typography>
                    </td>
                    {/* Recursos — veículo + motorista */}
                    <td className={classes.td}>
                      <Box className={classes.resRow}>
                        <LocalShippingIcon className={classes.resIcon} />
                        <span className={route.vehicle?.plate ? classes.plate : classes.resNone}>{route.vehicle?.plate || '— sem viatura'}</span>
                      </Box>
                      <Box className={classes.resRow} style={{ marginTop: 3 }}>
                        <PersonIcon className={classes.resIcon} />
                        <span className={route.driver?.name ? classes.resTxt : classes.resNone}>{route.driver?.name || '— sem motorista'}</span>
                      </Box>
                    </td>
                    {/* Estado + envio ao veículo */}
                    <td className={classes.td}>
                      <span className={classes.badge} style={{ background: st.bg, color: st.color }}>{st.label}</span>
                      {route.sentToVehicle && (
                        <Box style={{ display: 'flex', alignItems: 'center', gap: 3, marginTop: 4 }}>
                          <CheckCircleIcon style={{ fontSize: 12, color: '#16a34a' }} />
                          <Typography style={{ fontSize: '0.64rem', color: '#16a34a', fontWeight: 700 }}>Enviada</Typography>
                        </Box>
                      )}
                    </td>
                    {/* Criada — data + autor */}
                    <td className={classes.td}>
                      <Typography style={{ fontSize: '0.78rem', color: COLORS.text }}>{formatDate(route.createdAt)}</Typography>
                      <Typography style={{ fontSize: '0.66rem', color: COLORS.textMuted }}>{route.creator?.name || '—'}</Typography>
                    </td>
                    <td className={classes.td}>
                      <IconButton size="small" onClick={(e) => handleDelete(e, route.id)} className={classes.deleteBtn}>
                        <DeleteIcon style={{ fontSize: 16 }} />
                      </IconButton>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Box>
      )}

      {/* Detail Popup */}
      {selectedRoute && (
        <Box className={classes.overlay} onClick={() => setSelectedRoute(null)}>
          <Box className={classes.detailDialog} onClick={e => e.stopPropagation()}>
            <Box className={classes.detailHeader}>
              <Box style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <Box style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(59,130,246,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <DirectionsIcon style={{ color: '#60a5fa', fontSize: 18 }} />
                </Box>
                <Box>
                  <Typography style={{ fontSize: '0.95rem', fontWeight: 700 }}>
                    {selectedRoute.name || 'Rota sem nome'}
                  </Typography>
                  <Typography style={{ fontSize: '0.65rem', color: '#94a3b8' }}>
                    ID: {selectedRoute.id} — {formatDateTime(selectedRoute.createdAt)}
                  </Typography>
                </Box>
              </Box>
              <IconButton size="small" onClick={() => setSelectedRoute(null)} style={{ color: '#94a3b8' }}>
                <CloseIcon style={{ fontSize: 18 }} />
              </IconButton>
            </Box>

            <Box className={classes.detailBody}>
              {/* Status */}
              <Box className={classes.detailSection}>
                <Box style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {(() => {
                    const st = STATUS_STYLES[selectedRoute.status] || STATUS_STYLES.draft;
                    return <span className={classes.badge} style={{ background: st.bg, color: st.color }}>{st.label}</span>;
                  })()}
                  {selectedRoute.sentToVehicle && (
                    <span className={classes.badge} style={{ background: 'rgba(34,197,94,0.08)', color: '#22c55e' }}>
                      Enviada para viatura
                    </span>
                  )}
                </Box>
              </Box>

              {/* Percurso completo */}
              <Box className={classes.detailSection}>
                <Typography className={classes.detailSectionLabel}>
                  Percurso {selectedRoute.waypoints?.length > 0 ? `(${selectedRoute.waypoints.length + 2} pontos)` : ''}
                </Typography>
                <Box className={classes.detailCard}>
                  <Box className={classes.timeline}>
                    <Box className={classes.timelineLine} />

                    {/* Origem */}
                    <Box className={classes.waypointItem}>
                      <Box className={classes.waypointDot} style={{ background: '#22c55e', boxShadow: '0 0 6px rgba(34,197,94,0.4)' }} />
                      <Box style={{ flex: 1, minWidth: 0 }}>
                        <Typography className={classes.waypointLabel} style={{ color: '#22c55e' }}>Origem</Typography>
                        <Typography className={classes.waypointName}>{selectedRoute.origin?.name || '-'}</Typography>
                      </Box>
                    </Box>

                    {/* Pontos intermedios */}
                    {selectedRoute.waypoints && selectedRoute.waypoints.length > 0 && selectedRoute.waypoints.map((wp, idx) => (
                      <Box key={idx} className={classes.waypointItem}>
                        <Box className={classes.waypointDot} style={{ background: '#3b82f6', boxShadow: '0 0 6px rgba(59,130,246,0.3)' }} />
                        <Box style={{ flex: 1, minWidth: 0 }}>
                          <Typography className={classes.waypointLabel} style={{ color: '#3b82f6' }}>
                            Ponto {idx + 1}
                          </Typography>
                          <Typography className={classes.waypointName}>
                            {wp.name || wp.address || `${wp.lat?.toFixed(5)}, ${wp.lng?.toFixed(5)}`}
                          </Typography>
                        </Box>
                      </Box>
                    ))}

                    {/* Destino */}
                    <Box className={classes.waypointItem}>
                      <Box className={classes.waypointDot} style={{ background: '#ef4444', boxShadow: '0 0 6px rgba(239,68,68,0.4)' }} />
                      <Box style={{ flex: 1, minWidth: 0 }}>
                        <Typography className={classes.waypointLabel} style={{ color: '#ef4444' }}>Destino</Typography>
                        <Typography className={classes.waypointName}>{selectedRoute.destination?.name || '-'}</Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Box>

              {/* Estatisticas */}
              <Box className={classes.detailSection}>
                <Typography className={classes.detailSectionLabel}>Estatisticas</Typography>
                <Box className={classes.detailStatsGrid}>
                  <Box className={classes.detailStatCard}>
                    <Box className={classes.detailStatIcon} style={{ background: 'rgba(59,130,246,0.08)' }}>
                      <DirectionsIcon style={{ fontSize: 16, color: '#3b82f6' }} />
                    </Box>
                    <Box>
                      <Typography style={{ fontSize: '0.88rem', fontWeight: 700, color: 'var(--adm-text, #1e293b)' }}>{formatDistance(selectedRoute.totalDistanceM)}</Typography>
                      <Typography style={{ fontSize: '0.6rem', color: 'var(--adm-text-muted, #94a3b8)', textTransform: 'uppercase' }}>Distancia</Typography>
                    </Box>
                  </Box>
                  <Box className={classes.detailStatCard}>
                    <Box className={classes.detailStatIcon} style={{ background: 'rgba(245,158,11,0.08)' }}>
                      <TimerIcon style={{ fontSize: 16, color: '#f59e0b' }} />
                    </Box>
                    <Box>
                      <Typography style={{ fontSize: '0.88rem', fontWeight: 700, color: 'var(--adm-text, #1e293b)' }}>{formatDuration(selectedRoute.totalDurationS)}</Typography>
                      <Typography style={{ fontSize: '0.6rem', color: 'var(--adm-text-muted, #94a3b8)', textTransform: 'uppercase' }}>Duracao</Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>

              {/* Paragens de descanso */}
              {selectedRoute.routeDataSummary?.stops?.length > 0 && (
                <Box className={classes.detailSection}>
                  <Typography className={classes.detailSectionLabel}>
                    Paragens de Descanso ({selectedRoute.routeDataSummary.stops.length})
                  </Typography>
                  <Box className={classes.detailCard} style={{ padding: '4px 12px' }}>
                    <Box className={classes.stopTimeline}>
                      {selectedRoute.routeDataSummary.stops.map((stop, idx) => {
                        const st = STOP_TYPES[stop.type] || STOP_TYPES['rest'];
                        return (
                          <Box key={idx} className={classes.stopItem}>
                            <Box className={classes.stopIcon} style={{ background: st.bg }}>
                              {stop.type === 'daily-rest' || stop.type === 'rest'
                                ? <HotelIcon style={{ fontSize: 14, color: st.color }} />
                                : <PauseCircleFilledIcon style={{ fontSize: 14, color: st.color }} />
                              }
                            </Box>
                            <Box className={classes.stopInfo}>
                              <Typography className={classes.stopAddress}>
                                {stop.address || `Paragem ${idx + 1}`}
                              </Typography>
                              <Box className={classes.stopMeta}>
                                <span className={classes.stopTypeBadge} style={{ background: st.bg, color: st.color }}>
                                  {st.label}
                                </span>
                                {stop.kmAtStop != null && (
                                  <Typography className={classes.stopMetaItem}>
                                    <DirectionsIcon style={{ fontSize: 10 }} />
                                    {(stop.kmAtStop / 1000).toFixed(0)} km
                                  </Typography>
                                )}
                                {stop.drivingTime != null && (
                                  <Typography className={classes.stopMetaItem}>
                                    <TimerIcon style={{ fontSize: 10 }} />
                                    {formatStopDuration(Math.round(stop.drivingTime / 60))}
                                  </Typography>
                                )}
                                {stop.stopDuration != null && (
                                  <Typography className={classes.stopMetaItem}>
                                    <PauseCircleFilledIcon style={{ fontSize: 10 }} />
                                    {formatStopDuration(Math.round(stop.stopDuration / 60))}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                </Box>
              )}

              {/* Atribuicao */}
              <Box className={classes.detailSection}>
                <Typography className={classes.detailSectionLabel}>Atribuicao</Typography>
                <Box className={classes.detailCard}>
                  <Box className={classes.detailRow}>
                    <LocalShippingIcon style={{ fontSize: 14, color: '#3b82f6' }} />
                    <Typography className={classes.detailLabel}>Veiculo:</Typography>
                    <Typography className={classes.detailValue}>{selectedRoute.vehicle?.plate || 'Sem viatura'}</Typography>
                  </Box>
                  <Box className={classes.detailRow}>
                    <PersonIcon style={{ fontSize: 14, color: '#8b5cf6' }} />
                    <Typography className={classes.detailLabel}>Motorista:</Typography>
                    <Typography className={classes.detailValue}>{selectedRoute.driver?.name || 'Sem motorista'}</Typography>
                  </Box>
                </Box>
              </Box>

              {/* Info adicional */}
              <Box className={classes.detailSection}>
                <Typography className={classes.detailSectionLabel}>Informacao</Typography>
                <Box className={classes.detailCard}>
                  <Box className={classes.detailRow}>
                    <Typography className={classes.detailLabel}>Organização:</Typography>
                    <Typography className={classes.detailValue}>{selectedRoute.client?.name || '-'}</Typography>
                  </Box>
                  <Box className={classes.detailRow}>
                    <Typography className={classes.detailLabel}>Criado por:</Typography>
                    <Typography className={classes.detailValue}>
                      {selectedRoute.creator?.name || '-'}
                      {selectedRoute.creator?.email && <span style={{ color: 'var(--adm-text-muted, #94a3b8)', fontWeight: 400 }}> ({selectedRoute.creator.email})</span>}
                    </Typography>
                  </Box>
                  {selectedRoute.cargoRef && (
                    <Box className={classes.detailRow}>
                      <Typography className={classes.detailLabel}>Ref. Carga:</Typography>
                      <Typography className={classes.detailValue}>{selectedRoute.cargoRef}</Typography>
                    </Box>
                  )}
                  <Box className={classes.detailRow}>
                    <Typography className={classes.detailLabel}>Criada em:</Typography>
                    <Typography className={classes.detailValue}>{formatDateTime(selectedRoute.createdAt)}</Typography>
                  </Box>
                  {selectedRoute.updatedAt && selectedRoute.updatedAt !== selectedRoute.createdAt && (
                    <Box className={classes.detailRow}>
                      <Typography className={classes.detailLabel}>Atualizada:</Typography>
                      <Typography className={classes.detailValue}>{formatDateTime(selectedRoute.updatedAt)}</Typography>
                    </Box>
                  )}
                </Box>
              </Box>

              {/* Envio para viatura */}
              {selectedRoute.sentToVehicle && (
                <Box className={classes.detailSection} style={{ marginBottom: 0 }}>
                  <Typography className={classes.detailSectionLabel}>Envio para Viatura</Typography>
                  <Box className={classes.detailCard} style={{ background: '#f0fdf4', borderColor: '#bbf7d0' }}>
                    <Box className={classes.detailRow}>
                      <SendIcon style={{ fontSize: 14, color: '#22c55e' }} />
                      <Typography className={classes.detailLabel}>Estado:</Typography>
                      <Typography className={classes.detailValue} style={{ color: '#22c55e' }}>Enviada</Typography>
                    </Box>
                    {selectedRoute.sentToVehicleAt && (
                      <Box className={classes.detailRow}>
                        <CalendarTodayIcon style={{ fontSize: 13, color: '#22c55e' }} />
                        <Typography className={classes.detailLabel}>Data envio:</Typography>
                        <Typography className={classes.detailValue}>{formatDateTime(selectedRoute.sentToVehicleAt)}</Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              )}
            </Box>

            <Box className={classes.detailFooter}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<DeleteIcon style={{ fontSize: 15 }} />}
                onClick={(e) => handleDelete(e, selectedRoute.id)}
                style={{ color: 'var(--adm-danger, #ef4444)', borderColor: '#fecaca', fontSize: '0.78rem', textTransform: 'none', borderRadius: 8 }}
              >
                Eliminar
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={() => setSelectedRoute(null)}
                style={{ fontSize: '0.78rem', textTransform: 'none', borderRadius: 8, color: 'var(--adm-text-dim, #64748b)', borderColor: 'var(--adm-border, #e2e8f0)' }}
              >
                Fechar
              </Button>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
}

export default AdminRoutes;
